import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Box, Typography, Button } from '@mui/material';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';

// Global one-time popup: shown once to the employee a deduction gets
// recorded against, naming who registered it and why - never shown again
// for that same deduction once dismissed (seen_at). Catches deductions
// added while this tab is open (Realtime) and any that were already
// waiting from before the tab opened (the initial fetch). Deliberately
// framed as a cheerful "thank you" rather than a scolding - the sarcasm
// is the point.
const DeductionAlert = () => {
  const { currentUser } = useAuth();
  const [queue, setQueue] = useState([]);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    fetchUnseen();

    const channel = supabase
      .channel(`employee-deductions-${currentUser.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'employee_deductions', filter: `user_id=eq.${currentUser.id}` },
        async (payload) => {
          const row = await resolveCreator(payload.new);
          setQueue((prev) => [...prev, row]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const resolveCreator = async (row) => {
    if (!row.created_by) return { ...row, creatorName: null };
    const { data } = await supabase.from('users').select('name').eq('id', row.created_by).single();
    return { ...row, creatorName: data?.name || null };
  };

  const fetchUnseen = async () => {
    const { data, error } = await supabase
      .from('employee_deductions')
      .select('id, amount, deduction_type, days, reason, created_at, created_by, users!employee_deductions_created_by_fkey(name)')
      .eq('user_id', currentUser.id)
      .is('seen_at', null)
      .order('created_at', { ascending: true });
    if (error) {
      console.error('Error fetching unseen deductions:', error);
      return;
    }
    setQueue((data || []).map((d) => ({ ...d, creatorName: d.users?.name || null })));
  };

  const current = queue[0] || null;

  const dismiss = async () => {
    if (!current) return;
    setDismissing(true);
    const { error } = await supabase.from('employee_deductions').update({ seen_at: new Date().toISOString() }).eq('id', current.id);
    setDismissing(false);
    if (error) {
      console.error('Error marking deduction as seen:', error);
      return;
    }
    setQueue((prev) => prev.slice(1));
  };

  const headline =
    current?.deduction_type === 'days'
      ? `اتخصم ${Number(current.days).toLocaleString('ar-EG')} يوم من مرتبك`
      : current
      ? `اتخصم ${Number(current.amount).toLocaleString('ar-EG')} جنيه من مرتبك`
      : '';

  return (
    <Dialog open={!!current} maxWidth="xs" fullWidth onClose={() => {}}>
      {current && (
        <>
          <Box sx={{ bgcolor: '#fbc02d', color: '#4e342e', p: 4, textAlign: 'center' }}>
            <EmojiEmotionsIcon sx={{ fontSize: 72, color: '#fff' }} />
            <Typography variant="h6" sx={{ mt: 1.5, fontWeight: 'bold' }}>
              مبروووك! 🎉
            </Typography>
          </Box>
          <DialogContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#e65100' }}>
              {headline}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 'bold' }}>
              شكراً إنك وفرت فلوس للشركة! 😂
            </Typography>
            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>مين اللي سجله:</strong> {current.creatorName || '—'}
              </Typography>
              <Typography variant="body1">
                <strong>السبب:</strong> {current.reason}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              variant="contained"
              sx={{ bgcolor: '#fbc02d', color: '#4e342e', '&:hover': { bgcolor: '#f9a825' } }}
              disabled={dismissing}
              onClick={dismiss}
            >
              العفو 😅
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default DeductionAlert;
